<template>
  <section class="credentials-panel">
    <header class="credentials-header">
      <h3 class="credentials-title">{{ $t('accessCredentials.title') }}</h3>
      <p class="credentials-hint">{{ $t(saved ? 'accessCredentials.savedHint' : 'accessCredentials.hint') }}</p>
    </header>

    <div v-if="!saved" class="credentials-fields">
      <label class="credentials-label">
        {{ $t('accessCredentials.password') }}
        <input :value="password" @input="$emit('update:password', $event.target.value)" :type="visible ? 'text' : 'password'" autocomplete="new-password" class="credentials-input" :aria-invalid="Boolean(passwordError)" />
        <span v-if="passwordError" class="credentials-error">{{ passwordError }}</span>
      </label>
      <label class="credentials-label">
        {{ $t('accessCredentials.confirm') }}
        <input :value="confirmation" @input="$emit('update:confirmation', $event.target.value)" :type="visible ? 'text' : 'password'" autocomplete="new-password" class="credentials-input" :aria-invalid="Boolean(confirmationError)" />
        <span v-if="confirmationError" class="credentials-error">{{ confirmationError }}</span>
      </label>
    </div>
    <div v-else class="credentials-fields credentials-fields-saved">
      <label class="credentials-label">{{ $t('accessCredentials.link') }}
        <input :value="`${window.location.origin}/login`" readonly class="credentials-input" />
      </label>
      <label class="credentials-label">{{ $t('accessCredentials.user') }}
        <input :value="email" readonly class="credentials-input" />
      </label>
      <label class="credentials-label">{{ $t('accessCredentials.password') }}
        <input :value="password" :type="visible ? 'text' : 'password'" readonly class="credentials-input" />
      </label>
    </div>
    <div class="credentials-actions">
      <button v-if="!saved" type="button" @click="generate" class="credentials-button credentials-button-primary">{{ $t('accessCredentials.generate') }}</button>
      <button type="button" @click="visible = !visible" class="credentials-button credentials-button-secondary">{{ $t(visible ? 'accessCredentials.hide' : 'accessCredentials.show') }}</button>
      <button v-if="saved" type="button" @click="copy" class="credentials-button credentials-button-primary">{{ $t('accessCredentials.copy') }}</button>
    </div>
    <p v-if="saved" class="credentials-footnote">{{ $t('accessCredentials.copyHint') }}</p>
  </section>
</template>

<script setup>
import { ref } from 'vue';
import { toast } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  password: { type: String, default: '' },
  confirmation: { type: String, default: '' },
  email: { type: String, default: '' },
  saved: { type: Boolean, default: false },
  passwordError: { type: String, default: '' },
  confirmationError: { type: String, default: '' },
});
const emit = defineEmits(['update:password', 'update:confirmation']);
const { t } = useI18n();
const visible = ref(false);
const window = globalThis.window;

function generate() {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@';
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const value = Array.from(bytes, byte => alphabet[byte & 63]).join('');
  emit('update:password', value);
  emit('update:confirmation', value);
  visible.value = true;
}

async function copy() {
  try {
    await navigator.clipboard.writeText(`${t('accessCredentials.link')}: ${window.location.origin}/login\n${t('accessCredentials.user')}: ${props.email}\n${t('accessCredentials.password')}: ${props.password}`);
    toast.success(t('accessCredentials.copied'));
  } catch {
    toast.error(t('accessCredentials.copyFailed'));
  }
}
</script>

<style scoped>
.credentials-panel {
  box-sizing: border-box;
  container-type: inline-size;
  width: 100%;
  padding: 24px;
  border: 1px solid #a8c5d1;
  border-radius: 14px;
  background: #f4fafc;
  color: #385660;
}

.credentials-header {
  padding-bottom: 16px;
  border-bottom: 1px solid #d5e5eb;
}

.credentials-title {
  margin: 0;
  font-size: 17px;
  font-weight: 650;
  line-height: 1.4;
}

.credentials-hint {
  max-width: 65ch;
  margin: 5px 0 0;
  font-size: 14px;
  line-height: 1.5;
}

.credentials-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 20px;
}

.credentials-fields > .credentials-label {
  flex: 1 1 280px;
}

.credentials-fields-saved > .credentials-label {
  flex-basis: 235px;
}

.credentials-label {
  display: block;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.credentials-input {
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: 44px;
  margin-top: 7px;
  padding: 0 12px;
  border: 1px solid #91aeb9;
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: #254650;
  font: inherit;
  font-size: 15px;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.credentials-input:focus-visible {
  border-color: #1599bd;
  box-shadow: 0 0 0 3px rgb(37 176 215 / 20%);
}

.credentials-input[aria-invalid='true'] {
  border-color: #c44949;
}

.credentials-input[readonly] {
  background: #fff;
}

.credentials-error {
  display: block;
  margin-top: 6px;
  color: #ae3333;
  font-size: 13px;
  line-height: 1.4;
}

.credentials-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
}

.credentials-button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  padding: 9px 17px;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;
}

.credentials-button:focus-visible {
  outline: 3px solid rgb(37 176 215 / 35%);
  outline-offset: 2px;
}

.credentials-button-primary {
  background: #25b0d7;
  color: #fff;
}

.credentials-button-primary:hover {
  background: #168fb3;
}

.credentials-button-secondary {
  border-color: #92b0bd;
  background: #fff;
  color: #385660;
}

.credentials-button-secondary:hover {
  border-color: #497687;
  background: #eaf5f9;
}

.credentials-footnote {
  margin: 14px 0 0;
  font-size: 13px;
  line-height: 1.5;
}

@container (max-width: 440px) {
  .credentials-actions > .credentials-button {
    flex: 1 1 100%;
  }
}

@media (max-width: 640px) {
  .credentials-panel {
    padding: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .credentials-input,
  .credentials-button {
    transition: none;
  }
}
</style>
